import { useEffect, useState } from 'react'
import blueprintBackground from './assets/blueprint-bg.jpg'
import ConnectionParametersPanel from './components/ConnectionParametersPanel'
import SetupGuidePanel from './components/SetupGuidePanel'
import VerificationStatusRow from './components/VerificationStatusRow'
import { STATUS_ITEMS, SYSTEM_VERIFICATION_COPY } from './constants/systemVerificationContent'
import { useSystemVerification } from './hooks/useSystemVerification'

// ── Version Status Badge ───────────────────────────────────────────────────────
function AgentVersionBadge({ versionStatus, installedVersion, latestVersion }) {
  if (!installedVersion) return null

  const configs = {
    UP_TO_DATE: {
      icon: 'verified',
      label: `v${installedVersion} — Up to date`,
      className: 'bg-green-50 text-green-700 border-green-200',
      iconClass: 'text-green-600',
    },
    UPDATE_AVAILABLE: {
      icon: 'update',
      label: `v${installedVersion} — Update available (v${latestVersion})`,
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      iconClass: 'text-amber-500',
    },
    OUTDATED_BLOCKED: {
      icon: 'error',
      label: `v${installedVersion} — Outdated`,
      className: 'bg-red-50 text-red-700 border-red-200',
      iconClass: 'text-red-500',
    },
  }
  const cfg = configs[versionStatus] || configs.UP_TO_DATE

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${cfg.className}`}
    >
      <span className={`material-symbols-outlined text-sm ${cfg.iconClass}`} style={{ fontVariationSettings: "'FILL' 1" }}>
        {cfg.icon}
      </span>
      {cfg.label}
    </span>
  )
}

export default function SystemVerificationScreen() {
  const {
    statusMap,
    hasStartedScan,
    isAwaitingPermissionDecision,
    isScanning,
    isVerifying,
    isReadyToContinue,
    error,
    failureCause,
    downloadOptions,
    versionPolicy,
    versionStatus,
    installedVersion,
    startVerificationScan,
    retryVerification,
    completeVerification,
    goBackToLogin,
  } = useSystemVerification()

  const [copiedMacCmd, setCopiedMacCmd] = useState(false)

  const isMac = typeof navigator !== 'undefined' && String(navigator.userAgent || '').toLowerCase().includes('mac')
  const directDownloadLabel = isMac ? 'Download for macOS (.dmg)' : 'Download for Windows (.exe)'
  const directDownloadHref = isMac ? versionPolicy.macDownloadUrl : versionPolicy.windowsDownloadUrl

  useEffect(() => {
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
  }, [])

  const handleCopyMacCmd = () => {
    navigator.clipboard.writeText(SYSTEM_VERIFICATION_COPY.setupMacTestingCommand)
    setCopiedMacCmd(true)
    setTimeout(() => setCopiedMacCmd(false), 2500)
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface font-body text-on-surface antialiased">
      <main
        className="relative flex flex-1 flex-col items-center justify-center bg-cover bg-center py-8 px-4 sm:px-6"
        style={{
          backgroundImage: `linear-gradient(rgba(247,249,251,0.94), rgba(247,249,251,0.94)), url(${blueprintBackground})`,
        }}
      >
        <div className="pointer-events-none absolute inset-0 opacity-20">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#00327d" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <section className="relative z-10 w-full max-w-xl">
          <header className="mb-6 flex flex-col items-center">
            <h1 className="mb-1 font-headline text-3xl font-black tracking-tight text-primary">
              {SYSTEM_VERIFICATION_COPY.brand}
            </h1>
            <p className="font-label text-xs font-bold uppercase tracking-[0.2em] text-on-surface-variant/60">
              {SYSTEM_VERIFICATION_COPY.brandSubtitle}
            </p>
          </header>

          <div className="overflow-hidden rounded-lg bg-surface-container-lowest shadow-ambient outline outline-1 outline-outline-variant/20">
            {/* Header Hero */}
            <div className="bg-surface-container-low px-8 py-8 text-center">
              <div className="relative mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-fixed">
                <span className="material-symbols-outlined text-3xl text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                  verified_user
                </span>
                <span className={`absolute inset-0 rounded-full border-2 border-primary/20 ${isScanning ? 'animate-pulse' : ''}`} />
              </div>
              <h2 className="mb-1 font-headline text-3xl font-extrabold tracking-tight text-on-surface">
                {SYSTEM_VERIFICATION_COPY.title}
              </h2>
              <p className="text-xs text-on-surface-variant max-w-md mx-auto">
                4-Point Integrity Verification: Browser Network Access, Agent Daemon, Version v{versionPolicy.latestVersion}, and OS Telemetry Permissions.
              </p>
            </div>

            {/* ── The 4-Point Verification Checklist ──────────────────────── */}
            <div className="space-y-2 bg-surface-container-lowest px-8 py-4">
              {STATUS_ITEMS.map((item) => (
                <div key={item.key} className="relative">
                  <VerificationStatusRow
                    label={item.label}
                    icon={item.icon}
                    state={statusMap[item.key]}
                  />
                  {/* Attached Version Badge for Version Check Row */}
                  {item.key === 'versionCheck' && installedVersion && (
                    <div className="absolute right-24 top-1/2 -translate-y-1/2 hidden sm:block">
                      <AgentVersionBadge
                        versionStatus={versionStatus}
                        installedVersion={installedVersion}
                        latestVersion={versionPolicy.latestVersion}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* ── Contextual Self-Service Resolution Cards ───────────────── */}

            {/* 1. Browser Local Network Access Blocked */}
            {failureCause === 'browser_blocked' && (
              <div className="mx-8 mt-2 overflow-hidden rounded-lg border border-blue-200 bg-blue-50/90 p-4" role="alert">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-xl text-blue-600">lock_open</span>
                  <div className="flex-1 text-xs text-blue-900 leading-relaxed">
                    <p className="font-bold text-sm text-blue-950 mb-1">Action Required: Enable Local Network Access</p>
                    <ol className="list-decimal pl-4 space-y-1">
                      <li>Click the <strong>Tune / Lock icon (🔒)</strong> in your browser address bar (top-left).</li>
                      <li>Open <strong>Site settings</strong>.</li>
                      <li>Find <strong>Local network access</strong> (or <strong>Apps on device</strong>) and set it to <strong>Allow</strong>.</li>
                      <li>Reload and click <strong>Retry Verification</strong> below.</li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {/* 2. Desktop Agent Not Running */}
            {failureCause === 'agent_not_running' && (
              <div className="mx-8 mt-2 overflow-hidden rounded-lg border border-amber-200 bg-amber-50/90 p-4" role="alert">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-xl text-amber-600">downloading</span>
                  <div className="flex-1 text-xs text-amber-900 leading-relaxed">
                    <p className="font-bold text-sm text-amber-950 mb-1">Action Required: Install or Start Bridge Agent</p>
                    <p className="mb-2">The Bridge Agent was not detected on this machine. Follow these 3 simple steps:</p>
                    <ol className="list-decimal pl-4 space-y-1 mb-3">
                      <li>Download the agent installer using the button below.</li>
                      <li>Run the installer (if Windows SmartScreen asks, click &quot;More info&quot; → &quot;Run anyway&quot;).</li>
                      <li>Once the agent icon appears in your taskbar / menu bar, click <strong>Retry Verification</strong>.</li>
                    </ol>
                    <a
                      href={directDownloadHref}
                      className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-amber-700 transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm">download</span>
                      {directDownloadLabel}
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* 3. Version Outdated (Hard Block) */}
            {failureCause === 'version_outdated' && (
              <div className="mx-8 mt-2 overflow-hidden rounded-lg border border-red-200 bg-red-50/95 p-4" role="alert">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-xl text-red-600" style={{ fontVariationSettings: "'FILL' 1" }}>
                    system_update
                  </span>
                  <div className="flex-1 text-xs text-red-900 leading-relaxed">
                    <p className="font-bold text-sm text-red-950 mb-1">Mandatory Agent Update Required</p>
                    <p className="mb-2">
                      Your current agent (v{installedVersion}) is outdated. Version <strong>v{versionPolicy.latestVersion}</strong> is required for ambient background domain monitoring and multi-monitor telemetry.
                    </p>
                    {versionPolicy.releaseNotes && (
                      <p className="mb-3 italic text-red-800 bg-red-100/60 rounded p-1.5 text-[11px]">
                        What&apos;s new in v{versionPolicy.latestVersion}: {versionPolicy.releaseNotes}
                      </p>
                    )}
                    <a
                      href={directDownloadHref}
                      className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-red-700 transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm">download</span>
                      Download Agent v{versionPolicy.latestVersion} ({isMac ? 'macOS .dmg' : 'Windows .exe'})
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* 4. macOS Accessibility Permission Required */}
            {failureCause === 'mac_accessibility' && (
              <div className="mx-8 mt-2 overflow-hidden rounded-lg border border-red-200 bg-red-50/95 p-4" role="alert">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-xl text-red-600">accessibility_new</span>
                  <div className="flex-1 text-xs text-red-900 leading-relaxed">
                    <p className="font-bold text-sm text-red-950 mb-1">Action Required: Enable macOS Accessibility</p>
                    <p className="mb-2">macOS security requires explicit permission to inspect background window titles:</p>
                    <ol className="list-decimal pl-4 space-y-1 mb-2">
                      <li>Open <strong>Apple Menu  → System Settings → Privacy & Security → Accessibility</strong>.</li>
                      <li>Find <strong>MMT Activity Agent</strong> and toggle the switch to <strong>ON (blue)</strong>.</li>
                      <li>If not listed, click <strong>+</strong>, choose MMT Activity Agent from <strong>/Applications</strong>, and turn it on.</li>
                      <li>Click <strong>Retry Verification</strong> below.</li>
                    </ol>
                    <div className="mt-2 pt-2 border-t border-red-200/60">
                      <p className="font-semibold text-red-950 mb-1">If app was downloaded from internet (Testing Build):</p>
                      <div className="flex items-center gap-2 rounded bg-white p-1.5 font-mono text-[11px] text-red-950 border border-red-200">
                        <code className="flex-1 select-all overflow-x-auto">{SYSTEM_VERIFICATION_COPY.setupMacTestingCommand}</code>
                        <button
                          type="button"
                          onClick={handleCopyMacCmd}
                          className="shrink-0 rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700 hover:bg-red-200 transition-colors"
                        >
                          {copiedMacCmd ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Generic Error fallback if failureCause didn't match */}
            {error && failureCause === 'none' && (
              <div className="mx-8 mt-2 whitespace-pre-line rounded-md bg-error/10 px-4 py-3 text-sm text-error" role="alert">
                {error}
              </div>
            )}

            {/* Waiting for user browser prompt decision */}
            {isAwaitingPermissionDecision && (
              <div className="mx-8 mt-2 rounded-md bg-secondary-container/40 px-4 py-3 text-sm text-on-surface-variant flex items-center gap-2" role="status">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
                {SYSTEM_VERIFICATION_COPY.waitingDecisionPrompt}
              </div>
            )}

            {/* ── Soft Update Available Notice (Non-blocking) ─────────────── */}
            {versionStatus === 'UPDATE_AVAILABLE' && hasStartedScan && (
              <div className="mx-8 mt-3 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-base text-amber-600">info</span>
                  <span>
                    Newer Agent <strong>v{versionPolicy.latestVersion}</strong> available. (Your v{installedVersion} is supported).
                  </span>
                </div>
                <a
                  href={directDownloadHref}
                  className="font-bold underline text-amber-800 hover:text-amber-950"
                >
                  Download
                </a>
              </div>
            )}

            {/* ── Interactive Setup & Permissions Guide Panel ─────────────── */}
            <div className="mt-4">
              <SetupGuidePanel downloadOptions={downloadOptions} />
            </div>

            {/* ── Action Buttons ──────────────────────────────────────────── */}
            <div className="flex flex-col gap-3 px-8 pb-8">
              {!hasStartedScan ? (
                <button
                  type="button"
                  onClick={startVerificationScan}
                  disabled={isScanning || isVerifying}
                  className="group flex items-center justify-center gap-2 rounded-md bg-gradient-to-br from-primary to-primary-container py-3.5 font-bold text-on-primary shadow-sm transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {SYSTEM_VERIFICATION_COPY.startCheckLabel}
                  <span className="material-symbols-outlined text-xl transition-transform group-hover:translate-x-1">
                    play_arrow
                  </span>
                </button>
              ) : null}

              <button
                type="button"
                onClick={completeVerification}
                disabled={!isReadyToContinue}
                className="group flex items-center justify-center gap-2 rounded-md bg-gradient-to-br from-primary to-primary-container py-3.5 font-bold text-on-primary shadow-sm transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isVerifying ? 'Verifying...' : SYSTEM_VERIFICATION_COPY.continueLabel}
                <span className="material-symbols-outlined text-xl transition-transform group-hover:translate-x-1">
                  arrow_forward
                </span>
              </button>

              {hasStartedScan ? (
                <button
                  type="button"
                  onClick={retryVerification}
                  disabled={isScanning || isVerifying}
                  className="flex items-center justify-center gap-2 rounded-md bg-surface-container-high py-3 font-bold text-on-surface-variant transition-colors hover:bg-surface-dim disabled:cursor-not-allowed disabled:opacity-60 text-sm"
                >
                  <span className={`material-symbols-outlined text-lg ${isScanning ? 'animate-spin' : ''}`}>
                    refresh
                  </span>
                  {SYSTEM_VERIFICATION_COPY.retryLabel}
                </button>
              ) : null}
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={goBackToLogin}
              className="flex items-center gap-2 font-label text-sm font-bold text-on-surface-variant transition-colors hover:text-primary"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              {SYSTEM_VERIFICATION_COPY.backToLoginLabel}
            </button>
          </div>
        </section>

        <ConnectionParametersPanel />
      </main>
    </div>
  )
}
